import classNames from "classnames";
import first from "lodash/first";
import map from "lodash/map";
import max from "lodash/max";
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import ReactResizeDetector from "react-resize-detector";
import { ReactComponent as IconAlert } from "../../../../icons/16x16/alert-circle.svg";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import useElementClientBoundingRect from "../../../../lib/useElementClientBoundingRect";
import { countPages } from "../../../../utils/customization/configuration";
import { getNoCoverDefinitions, hasCoverDefinition } from "../../../../utils/customization/cover";
import { isDesktopSize } from "../../../../utils/customization/device";
import { findPageDefinitionsWithValidCondition } from "../../../../utils/customization/finders";
import { getNumberPagesToAdd } from "../../../../utils/customization/page";
import {
  DEFAULT_ZOOM_LEVEL,
  getPpmm,
  getZoomLimitPageSizes,
  ZoomLimitPageSizes,
} from "../../../../utils/customization/zoom";
import BannerNotification from "../../../atoms/BannerNotification";
import CharacteristicsSummary from "../../../molecules/customization/CharacteristicsSummary";
import CoverContent from "../../../molecules/customization/CoverContent";
import PageList from "../../../molecules/customization/PageList";
import UndoRedo from "../../../molecules/customization/UndoRedo";
import NotificationsArea from "../../../organisms/NotificationsArea";
import "./style.scss";

const DEFAULT_PPMM = 3;
const MARGIN_WIDTH = 72; // list margin
const MARGIN_HEIGHT = 24 + 66 + 28; // margin + pageBar + pageCount
const ITEMS_MARGIN_WIDTH = 24;
const PAGELIST_TOP = 40;

const CustomizationContent: FunctionComponent = () => {
  const { configuration, content, dispatch, template } = useCustomizationContext();
  const [ppmm, setPpmm] = useState<number>(DEFAULT_PPMM);
  const [pagesByLineMargin, setPagesByLineMargin] = useState<boolean>(false);
  const intl = useIntl();

  const zoomLimitPageSizes: ZoomLimitPageSizes = useMemo(
    () => getZoomLimitPageSizes(DEFAULT_ZOOM_LEVEL, content.templateType),
    [content.templateType]
  );
  const pageListRef = useRef<HTMLDivElement>(null);
  const containerRect = useElementClientBoundingRect(pageListRef);

  let nbPagesLinked = 1;
  if (template.templateType === "book" && template.pageMode === "single_horizontal") {
    nbPagesLinked = 2;
  }

  const maxPageHeight = useMemo(() => {
    return max([
      ...map(map(template.pageDefinitions, "height"), "value"),
      ...map(map(template.presentationDefinitions, "height"), "value"),
    ]);
  }, [template.pageDefinitions, template.presentationDefinitions]);

  const maxPageWidth = useMemo(() => {
    return max([
      ...map(map(template.pageDefinitions, "width"), "value"),
      ...map(map(template.presentationDefinitions, "width"), "value"),
    ]);
  }, [template.pageDefinitions, template.presentationDefinitions]);

  const onResize = useCallback(
    (width: number | undefined, height: number | undefined) => {
      if (!width || !height) {
        setPpmm(DEFAULT_PPMM);
        return;
      }

      // hack to get width without scroll size
      const innerWidth = pageListRef?.current?.clientWidth || width;
      if (!maxPageHeight || !maxPageWidth) {
        setPpmm(DEFAULT_PPMM);
        return;
      }

      let limitedHeight = true;
      let marginWidth = ITEMS_MARGIN_WIDTH;
      if (template.templateType !== "book" && configuration?.ui) {
        if (
          !isDesktopSize() &&
          configuration.ui.minPagesByLine &&
          configuration.ui.minPagesByLine > 0
        ) {
          zoomLimitPageSizes.max.value =
            (innerWidth - MARGIN_WIDTH) / configuration.ui.minPagesByLine;
          zoomLimitPageSizes.min.value =
            (innerWidth - MARGIN_WIDTH) / configuration.ui.minPagesByLine;
          limitedHeight = false;
          setPagesByLineMargin(false);
        } else if (configuration.ui.maxPagesByLine && configuration.ui.maxPagesByLine > 0) {
          zoomLimitPageSizes.max.value =
            (innerWidth - MARGIN_WIDTH) / configuration.ui.maxPagesByLine;
          zoomLimitPageSizes.min.value =
            (innerWidth - MARGIN_WIDTH) / configuration.ui.maxPagesByLine;
          limitedHeight = false;
          marginWidth = 48;
          setPagesByLineMargin(true);
        }
      }

      setPpmm(
        getPpmm(
          template,
          DEFAULT_ZOOM_LEVEL,
          innerWidth,
          height,
          MARGIN_WIDTH,
          MARGIN_HEIGHT,
          marginWidth,
          maxPageWidth,
          maxPageHeight,
          zoomLimitPageSizes,
          limitedHeight
        )
      );
    },
    [
      configuration,
      maxPageHeight,
      maxPageWidth,
      setPagesByLineMargin,
      setPpmm,
      template,
      zoomLimitPageSizes,
    ]
  );

  useEffect(() => {
    if (containerRect) {
      onResize(containerRect.width, containerRect.height - PAGELIST_TOP);
    }
  }, [zoomLimitPageSizes, onResize, containerRect]);

  // todo: should be moved in CustomizationContainer
  useEffect(() => {
    const conditionFilteredPageDefinitions = findPageDefinitionsWithValidCondition(
      template,
      content,
      {},
      configuration?.options
    );
    if (conditionFilteredPageDefinitions === null) {
      return;
    }

    const noCoverPageDefinitions = getNoCoverDefinitions(conditionFilteredPageDefinitions);
    let definitionOfPageToAdd = conditionFilteredPageDefinitions.find(
      (definition) => definition.filterType === template.contentPageConfiguration.addNext.filterType
    );
    if (!definitionOfPageToAdd) {
      definitionOfPageToAdd = first(noCoverPageDefinitions);
    }

    let minPages = configuration?.pages?.min ? configuration.pages.min : 1;
    const pageCount = countPages(content, noCoverPageDefinitions);
    if (template.templateType === "print") {
      minPages = 1;
    }
    let nbPagesToCount = nbPagesLinked;
    if (
      definitionOfPageToAdd &&
      pageCount < minPages &&
      (content.pages.length > 0 || content.templateType !== "calendar")
    ) {
      nbPagesToCount = getNumberPagesToAdd(template, definitionOfPageToAdd, configuration);
      for (let i = pageCount; i < minPages; i += nbPagesToCount) {
        dispatch({
          configuration,
          pageDefinitionKey: definitionOfPageToAdd.key,
          type: "addPage",
          undoKey: "ignore",
        });
      }
    }

    dispatch({
      configuration: template.contentPageConfiguration,
      type: "setPageConfigMapping",
    });
    // eslint-disable-next-line
  }, []);

  const [coverLowQualityPicturesCount, setCoverLowQualityPicturesCount] = useState<number>(0);
  const [pagesLowQualityPicturesCount, setPagesLowQualityPicturesCount] = useState<number>(0);

  const lowQualityWarning =
    pagesLowQualityPicturesCount + coverLowQualityPicturesCount > 0 ? (
      <div className="CustomizationContent-pageList-lowQuality-warning">
        <BannerNotification
          icon={<IconAlert />}
          message={intl.formatMessage(
            { id: "kustom_PageList_LowQuality_count" },
            { count: pagesLowQualityPicturesCount + coverLowQualityPicturesCount }
          )}
        />
      </div>
    ) : null;

  return (
    <div
      className={classNames(
        "CustomizationContent",
        `CustomizationContent--zoom${DEFAULT_ZOOM_LEVEL}`,
        {
          "CustomizationContent--book": template.templateType === "book",
          "CustomizationContent--calendar": template.templateType === "calendar",
        }
      )}
    >
      <div className="CustomizationContent-notifications">
        <NotificationsArea />
      </div>
      <div
        className={classNames("CustomizationContent-pageList", {
          "CustomizationContent-pageList-pagesByLineMargin": pagesByLineMargin,
        })}
        ref={pageListRef}
      >
        <CharacteristicsSummary />

        {lowQualityWarning}

        {hasCoverDefinition(template.pageDefinitions) && (
          <CoverContent onLowQualityWarning={setCoverLowQualityPicturesCount} ppmm={ppmm} />
        )}

        <PageList onLowQualityWarning={setPagesLowQualityPicturesCount} ppmm={ppmm} />
        <ReactResizeDetector
          handleHeight={true}
          handleWidth={true}
          onResize={onResize}
          skipOnMount={true}
        />
      </div>
      <div className="CustomizationContent-UndoRedo">
        <UndoRedo />
      </div>
    </div>
  );
};

export default CustomizationContent;
